import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isFunction, isString, sortBy, get, groupBy, capitalize, uniqBy } from 'lodash'
import { getPricingPeriod } from 'utils/service'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { getRecurrenceLabel } from 'components/pricing/pricingMethods'

/**
 * Gets a property from an item using a getter which can be a function, string, or fallback to default properties
 * @param {Object} item - The item to get the property from
 * @param {string|Function} getter - The property name or function to get the value
 * @returns {*} - The retrieved value
 */
const getPropertyValue = (item, getter) => {
  if (isFunction(getter)) {
    return getter(item)
  }
  if (isString(getter)) {
    return item[getter]
  }
  return undefined
}

/**
 * Maps a list of objects to an array of objects with label and value properties
 * @param {Array} items - The array of objects to map
 * @param {string|Function} labelGetter - The property name or function to get the label
 * @param {string|Function} valueGetter - The property name or function to get the value
 * @returns {Array} - An array of objects with label and value properties sorted by label (case-insensitive)
 */
export const mapToLabelValue = (items = [], labelGetter, valueGetter) => {
  const mappedItems = items.map(item => {
    const label = getPropertyValue(item, labelGetter)
    const value = getPropertyValue(item, valueGetter)

    return { ...item, label, value }
  })

  return sortBy(mappedItems, item => item.label?.toLowerCase?.() || '')
}

/**
 * Extracts unique values from rows for a specific field
 * @param {Array} rows - The array of row objects
 * @param {string} field - The field name to extract values from
 * @returns {Array} - Array of unique values
 */
const getUniqueValuesFromRows = (rows = [], field) => {
  if (!rows || !rows.length) return []

  // Extract values from the specified field in each row
  const values = rows.map(row => row[field]).filter(Boolean)

  // Return unique values
  return [...new Set(values)]
}

/**
 * Hook to create filter options for Configured Service dialog
 * @param {Object} params - Parameters
 * @param {Array} params.rows - Optional rows to filter options by
 * @param {boolean} params.filterByRows - Flag to enable filtering options by rows
 * @returns {Object} - Filter options
 */
const useCreateCSFilterOptions = ({ rows = [], filterByRows = false } = {}) => {
  const globalFilters = useSelector(state => get(state, 'GlobalSearchReducer.globalFilterOptions', null))
  const { businessLines, materials, measure, pricingPeriod, pricingZones, recurrence, serviceAction } = globalFilters || {}
  const recurrenceOptions = useMemo(() => {
    let mappedRecurrence = mapToLabelValue(recurrence, getRecurrenceLabel, getRecurrenceLabel)

    // Filter options by values in rows if filterByRows is enabled
    if (filterByRows && rows.length > 0) {
      const validRecurrences = getUniqueValuesFromRows(
        rows.map(row => ({ ...row, recurrenceLabel: getRecurrenceLabel(row) })),
        'recurrenceLabel'
      )
      if (validRecurrences.length > 0) {
        mappedRecurrence = mappedRecurrence.filter(option => validRecurrences.includes(option.value))
      }
    }

    return mappedRecurrence.sort((a, b) => {
      const aHasRecurrencePer = a.recurrencePer && Number(a.recurrencePer) > 1
      const bHasRecurrencePer = b.recurrencePer && Number(b.recurrencePer) > 1

      if (aHasRecurrencePer && !bHasRecurrencePer) return 1
      if (!aHasRecurrencePer && bHasRecurrencePer) return -1
      return a.label?.toLowerCase?.() < b.label?.toLowerCase?.() ? -1 : 1
    })
  }, [recurrence, rows, filterByRows])

  const businessLineOptions = useMemo(() => {
    let mappedBusinessLines = (businessLines || [])
      .filter(({ active }) => active)
      .map(bl => ({
        label: bl.businessLineLabel,
        value: bl.businessLineLabel,
        businessLineType: bl.businessLineIcon ? bl.businessLineIcon.replace('.svg', '') : '',
      }))

    // Filter options by values in rows if filterByRows is enabled
    if (filterByRows && rows.length > 0) {
      const validBusinessLines = getUniqueValuesFromRows(rows, 'businessLine')
      if (validBusinessLines.length > 0) {
        mappedBusinessLines = mappedBusinessLines.filter(option => validBusinessLines.includes(option.value))
      }
    }

    return sortBy(mappedBusinessLines, item => item.label?.toLowerCase?.() || '')
  }, [businessLines, rows, filterByRows])

  const materialOptions = useMemo(() => {
    let mappedMaterials =
      materials?.map(m => ({
        label: m.materialType,
        value: m.materialType,
        color: m.materialColor,
      })) || []

    // Filter options by values in rows if filterByRows is enabled
    if (filterByRows && rows.length > 0) {
      const validMaterials = getUniqueValuesFromRows(rows, 'materialType')
      if (validMaterials.length > 0) {
        mappedMaterials = mappedMaterials.filter(option => validMaterials.includes(option.value))
      }
    }

    return sortBy(mappedMaterials, item => item.label?.toLowerCase?.() || '')
  }, [materials, rows, filterByRows])

  const measureOptions = useMemo(() => {
    let options =
      measure?.map(m => ({
        label: `${m.volume} ${getMeasureUnit(m.unit)}`,
        value: `${m.volume} ${m.unit}`,
        volume: m.volume, // Keep volume for grouping
        unit: m.unit, // Keep unit for sorting
      })) || []

    // Filter options by values in rows if filterByRows is enabled
    if (filterByRows && rows.length > 0) {
      const validMeasures = getUniqueValuesFromRows(rows, 'measureText')
      if (validMeasures.length > 0) {
        options = options.filter(option => validMeasures.includes(option.value))
      }
    }

    const groupedOptions = groupBy(options, 'unit')

    const sortedUnits = Object.keys(groupedOptions).sort()
    return sortedUnits.map(unit => sortBy(groupedOptions[unit], item => parseFloat(item.volume))).flat()
  }, [measure, rows, filterByRows])

  const serviceActionOptions = useMemo(() => {
    let mappedServiceActions =
      serviceAction?.map(sa => ({
        label: sa.actionType,
        value: sa.actionType,
      })) || []

    // Filter options by values in rows if filterByRows is enabled
    if (filterByRows && rows.length > 0) {
      const validActions = getUniqueValuesFromRows(rows, 'actionType')
      if (validActions.length > 0) {
        mappedServiceActions = mappedServiceActions.filter(option => validActions.includes(option.value))
      }
    }

    return sortBy(mappedServiceActions, item => item.label?.toLowerCase?.() || '')
  }, [serviceAction, rows, filterByRows])
  const methodOptions = useMemo(() => {
    const uniqueMethodTypes = [
      ...new Set(rows.map(row => row.methodType || row.methodName).filter(Boolean)),
    ]
    return sortBy(
      uniqueMethodTypes.map(methodType => ({ label: methodType, value: methodType })),
      item => item.label?.toLowerCase?.() || ''
    )
  }, [rows])
  const pricingPeriodOptions = useMemo(() => {
    let options =
      pricingPeriod?.map(pp => ({
        label: capitalize(getPricingPeriod(pp.periodPer, pp.periodInterval)),
        value: capitalize(getPricingPeriod(pp.periodPer, pp.periodInterval)),
        periodInterval: pp.periodInterval,
        periodPer: pp.periodPer,
      })) || []

    // Filter options by values in rows if filterByRows is enabled
    if (filterByRows && rows.length > 0) {
      const validPricingPeriods = getUniqueValuesFromRows(
        rows.map(row => ({
          ...row,
          pricingPeriodLabel: capitalize(getPricingPeriod(row.periodPer, row.periodInterval)),
        })),
        'pricingPeriodLabel'
      )
      if (validPricingPeriods.length > 0) {
        options = options.filter(option => validPricingPeriods.includes(option.value))
      }
    }

    const groupedOptions = groupBy(options, 'periodInterval')

    const sortedIntervals = Object.keys(groupedOptions).sort((a, b) => Number(a) - Number(b))

    const sortedOptions = sortedIntervals
      .map(interval => {
        return groupedOptions[interval].sort((a, b) => {
          const aHasPeriodPer = a.periodPer && Number(a.periodPer) > 1
          const bHasPeriodPer = b.periodPer && Number(b.periodPer) > 1

          if (aHasPeriodPer && !bHasPeriodPer) return 1
          if (!aHasPeriodPer && bHasPeriodPer) return -1
          return a.label?.toLowerCase?.() < b.label?.toLowerCase?.() ? -1 : 1
        })
      })
      .flat()

    return uniqBy(sortedOptions, 'value')
  }, [pricingPeriod, rows, filterByRows])
  const pricingZoneOptions = useMemo(() => {
    let mappedZones = mapToLabelValue(pricingZones, 'zoneName', 'zoneName')

    // Filter options by values in rows if filterByRows is enabled
    if (filterByRows && rows.length > 0) {
      const validZones = getUniqueValuesFromRows(rows, 'zoneName')
      if (validZones.length > 0) {
        mappedZones = mappedZones.filter(option => validZones.includes(option.value))
      }
    }

    return mappedZones
  }, [pricingZones, rows, filterByRows])

  return {
    businessLineOptions,
    materialOptions,
    measureOptions,
    methodOptions,
    pricingPeriodOptions,
    pricingZoneOptions,
    recurrenceOptions,
    serviceActionOptions,
  }
}

export default useCreateCSFilterOptions
